import { useEffect, useMemo, useRef, useState } from 'react'
import {
  fetchAnnotation,
  saveClustersToServer,
  withKeyReissue,
  syncSurveyAssets
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/api'
import {
  DEFAULT_AUTO_RECOGNITION_VALUE,
  POLL_INTERVAL,
  DEFAULT_ERROR,
  HM_CLUSTER,
  CLUSTER_FORM_MODE
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'

import { hasAnnotationsOfType } from '@aytm/app/stats/page/question-views/hm/components/controls/recognition-form/lib/utils'

export const useRecognitionForm = ({
  clusters,
  image,
  visualPosition,
  onChangeModel,
  changeClustersForm,
  onFetchCRUDKey,
  onUpdateData
}) => {
  const abortControllerRef = useRef(null)
  const timerRef = useRef(null)
  const isFirstRequestRef = useRef(true)

  const [annotationType, setAnnotationType] = useState(DEFAULT_AUTO_RECOGNITION_VALUE)
  const [isLoading, setIsLoading] = useState(false)
  const [isEmpty, setIsEmpty] = useState(false)
  const [error, setError] = useState(null)

  const clearPolling = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    isFirstRequestRef.current = true
  }

  // the banner shows what it is handed, so a thrown failure is read for its message rather than
  // rendered as itself
  const handleError = (error = null) => {
    setIsLoading(false)
    setError(('string' === typeof error ? error : error?.message) || DEFAULT_ERROR)
  }

  const updateSurveyAssets = clustersSaved => {
    syncSurveyAssets(clustersSaved, visualPosition)

    if (!clusters?.length) {
      onChangeModel({ clusters: clustersSaved })
    }

    // the block rebuilds its clusters from the survey when fresh answers arrive, which is what
    // adds these to the ones already drawn and gives them their statistics
    onUpdateData?.()
  }

  const saveRecognizedClusters = async recognizedClusters => {
    try {
      const { acquired, key } = await withKeyReissue({ onFetchCRUDKey }, async keyResponse => {
          const res = await saveClustersToServer({
            keyCode: keyResponse.code,
            clusters: recognizedClusters,
            imageHeight: image?.original?.height,
            visualPosition
          })

          const clustersSaved = res?.assets?.[HM_CLUSTER]
          if (clustersSaved?.length) {
            updateSurveyAssets(clustersSaved)
        }
      })

      if (!acquired) {
        handleError(key?.error)
        return
      }

      // a save is the end of the run, whether it kept any clusters or not: the form closes, and
      // only a failure keeps it open to show why
      handleCancelRecognition()
    } catch (error) {
      console.error('saveRecognizedClusters error:', error?.toString()) // eslint-disable-line no-console
      handleError(error)
    }
  }

  const handleCompleted = data => {
    clearPolling()
    if (data?.length) {
      saveRecognizedClusters(data)
    } else {
      setIsLoading(false)
      setIsEmpty(true)
    }
  }

  const handlePending = () => {
    timerRef.current = setTimeout(() => {
      isFirstRequestRef.current = false
      handleRunRecognition()
    }, POLL_INTERVAL)
  }

  const handleRunRecognition = async () => {
    try {
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      setIsLoading(true)
      setError(null)

      const response = await fetchAnnotation({
        imageId: image?.id,
        type: annotationType,
        isStart: isFirstRequestRef.current,
        signal: abortControllerRef.current.signal
      })

      if (response?.status === 'completed') {
        handleCompleted(response.data)
      } else if (response?.status === 'error') {
        clearPolling()
        handleError()
      } else {
        handlePending()
      }
    } catch (error) {
      if (error?.name === 'AbortError') {
        setIsLoading(false) // externally-triggered abort must not leave the spinner on
        return
      }
      handleError()
      clearPolling()
    }
  }

  const handleCancelRecognition = () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    clearPolling()

    setIsLoading(false)
    setIsEmpty(false)
    setError(null)

    changeClustersForm(CLUSTER_FORM_MODE.OFF)
  }

  useEffect(
    () => () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    },
    []
  )

  const hasAnnotationsOfCurrentType = useMemo(
    () => hasAnnotationsOfType(clusters, annotationType),
    [clusters, annotationType]
  )

  return {
    handleRunRecognition,
    handleCancelRecognition,
    hasAnnotationsOfCurrentType,
    error,
    isEmpty,
    isLoading,
    annotationType,
    setAnnotationType
  }
}
